import React, { type FC, useEffect, useState, useRef, useCallback } from 'react';

import axios from 'axios';
import { useTranslation } from 'react-i18next';


import { CreateEventForm } from './CreateEventForm';
import { useProcessingOverlay } from '../../context/ProcessingOverlayContext';
import { ReactComponent as ChangeBoatIcon } from '../../react-icons/double-arrow.svg';
import { ReactComponent as CrossIcon } from '../../react-icons/plusIcon.svg';
import { ReactComponent as UploadIcon } from '../../react-icons/uploadIcon.svg';
import { showMessage } from '../../showMessage';
import { getApiErrorMessage } from '../../utils/apiError';
import { pollUploadJob } from '../../utils/uploadJobPolling';

import type { SharingMetadata } from '../../types';
import '../../styles/ModalStyle.css';

interface ModalProperties {
  isVisible: boolean;
  onClose: () => void;
  userId: string;
}

interface EventsResponse {
  success: boolean;
  events: Event[];
}

interface Event {
  id: number;
  event_name: string;
  event_date: string | null;
  created_at: string;
  sharing?: SharingMetadata;
}

interface Boat {
  id: number;
  boat_name: string;
  sharing?: SharingMetadata;
}

interface GetUserBoatsResponse {
  success: boolean;
  userBoats: Array<{ id: number; name: string; sharing?: SharingMetadata }>;
}

interface UploadStartResponse {
  success: boolean;
  processing?: boolean;
  jobId?: string;
  message?: string;
}

const API_BASE_URL: string = process.env.REACT_APP_API_BASE_URL ?? '';
const NOTIF_ERROR = 'notifications.error' as const;
const ERR_MISSING_BASE_URL = 'errors.missingApiBase' as const;
const ERR_MISSING_TOKEN = 'errors.missingToken' as const;
const ERR_UNEXPECTED_FORMAT = 'modals.unexpectedError' as const;
const ERR_FETCH_BOATS = 'modals.errorFetchingBoats' as const;
const ERR_FETCH_EVENTS = 'modals.errorFetchingEvents' as const;
const MODALS_NO_FILE = 'modals.noFileSelected' as const;
const MODALS_NO_EVENT = 'modals.noEventSelected' as const;
const ACCEPTED_FILE_TYPES = '.csv,.gpx';
const MODALS_UPLOAD_AND_GENERATE = 'modals.uploadAndGenerate' as const;
const MODALS_UPLOAD_FILE = 'modals.uploadFile' as const;

const ModalReports: FC<ModalProperties> = ({ isVisible, onClose, userId }) => {
  const { t } = useTranslation('common');
  const fileInputReference = useRef<HTMLInputElement>(null);
  const [boats, setBoats] = useState<Boat[]>([]);
  const [selectedBoatId, setSelectedBoatId] = useState<number | null>(null);
  const [userEvents, setUserEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [hoveredEventId, setHoveredEventId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateEventForm, setShowCreateEventForm] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [_isUploading, setIsUploading] = useState(false);
  // Removed unused status message for now to satisfy lint
  const { startProcess, updateProcess, endProcess } = useProcessingOverlay();

  // Function to fetch user's boats
  const fetchBoats = useCallback(() => {
    setIsLoading(true);
    setError(null);
    if (!API_BASE_URL) {
      setError(t(ERR_MISSING_BASE_URL));
      setIsLoading(false);
      return;
    }
    const tokenRaw = localStorage.getItem('token');
    if (!tokenRaw) {
      setError(t(ERR_MISSING_TOKEN));
      setIsLoading(false);
      return;
    }
    const token: string = tokenRaw;
    const getBoatsUrl = `${API_BASE_URL}/api/user/getUserBoats`;
    axios
      .post<GetUserBoatsResponse>(
        getBoatsUrl,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      .then((response) => {
        if (response.data.success && Array.isArray(response.data.userBoats)) {
          const boatsData: Boat[] = response.data.userBoats
            .filter((boat) => (boat.sharing ? boat.sharing.isOwner : true))
            .map((boat) => ({
              id: boat.id,
              boat_name: boat.name,
            }));
          setBoats(boatsData);
          setSelectedBoatId((previousSelected) =>
            previousSelected !== null && boatsData.some((boat) => boat.id === previousSelected)
              ? previousSelected
              : null,
          );
        } else {
          setError(t(ERR_UNEXPECTED_FORMAT));
        }
        setIsLoading(false);
        return null;
      })
      .catch(() => {
        setError(t(ERR_FETCH_BOATS));
        setIsLoading(false);
        return null;
      });
  }, []);

  // Function to fetch events for the selected boat
  const fetchEvents = useCallback(() => {
    if (!selectedBoatId) {
      return;
    }
    setIsLoading(true);
    setError(null);
    if (!API_BASE_URL) {
      setError(t(ERR_MISSING_BASE_URL));
      setIsLoading(false);
      return;
    }
    const tokenRaw = localStorage.getItem('token');
    if (!tokenRaw) {
      setError(t(ERR_MISSING_TOKEN));
      setIsLoading(false);
      return;
    }
    const token: string = tokenRaw;
    const getEventsUrl = `${API_BASE_URL}/api/user/getEvents`;
    axios
      .post<EventsResponse>(
        getEventsUrl,
        { token, boat_id: selectedBoatId },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      .then((response) => {
        if (response.data.success && Array.isArray(response.data.events)) {
          const ownEvents = response.data.events.filter((event) =>
            event.sharing ? event.sharing.isOwner : true,
          );
          const sortedEvents = sortEvents(ownEvents);
          setUserEvents(sortedEvents);
          setSelectedEventId((previousSelected) =>
            previousSelected !== null && sortedEvents.some((event) => event.id === previousSelected)
              ? previousSelected
              : null,
          );
          setHoveredEventId((previousHovered) =>
            previousHovered !== null && sortedEvents.some((event) => event.id === previousHovered)
              ? previousHovered
              : null,
          );
        } else {
          setError(t(ERR_UNEXPECTED_FORMAT));
        }
        setIsLoading(false);
        return null;
      })
      .catch(() => {
        setError(t(ERR_FETCH_EVENTS));
        setIsLoading(false);
        return null;
      });
  }, [selectedBoatId]);

  useEffect(() => {
    if (isVisible && boats.length === 0) {
      fetchBoats();
    }
  }, [isVisible, boats.length, fetchBoats]);

  useEffect(() => {
    if (isVisible && selectedBoatId !== null) {
      fetchEvents();
    }
  }, [isVisible, selectedBoatId, fetchEvents]);

  const sortEvents = (events: Event[]): Event[] => {
    return [...events].sort((a, b) => {
      const timeA = a.event_date ? new Date(a.event_date).getTime() : null;
      const timeB = b.event_date ? new Date(b.event_date).getTime() : null;
      if (timeA !== null && timeB !== null) {
        return timeB - timeA;
      }
      if (timeA !== null) {
        return -1;
      }
      if (timeB !== null) {
        return 1;
      }
      return a.event_name.localeCompare(b.event_name);
    });
  };

  const handleUploadAreaClick = () => {
    fileInputReference.current?.click();
  };

  const handleCreateNewEvent = () => {
    setShowCreateEventForm(true);
  };

  const handleEventCreated = () => {
    setShowCreateEventForm(false);
    fetchEvents();
  };

  const handleFileUploadChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    const droppedFile = event.dataTransfer.files?.[0];
    if (droppedFile) {
      setFile(droppedFile);
      setFileName(droppedFile.name);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const uploadFile = (generateReport: boolean) => {
    if (!file) {
      showMessage('error', t(NOTIF_ERROR), t(MODALS_NO_FILE));
      return;
    }

    if (!selectedEventId) {
      showMessage('error', t(NOTIF_ERROR), t(MODALS_NO_EVENT));
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', userId);
    formData.append('event_id', selectedEventId.toString());

    if (!API_BASE_URL) {
      showMessage('error', t(NOTIF_ERROR), t(ERR_MISSING_BASE_URL));
      return;
    }
    const tokenRaw = localStorage.getItem('token');
    if (!tokenRaw) {
      showMessage('error', t(NOTIF_ERROR), t(ERR_MISSING_TOKEN));
      return;
    }
    const token: string = tokenRaw;
    const url = generateReport
      ? `${API_BASE_URL}/api/user/uploadCSVAndGenerateReport`
      : `${API_BASE_URL}/api/user/uploadCSV`;

    setIsUploading(true);
    const eventName = userEvents.find((eventItem) => eventItem.id === selectedEventId)?.event_name || t('common.labels.event');
    const processId = startProcess(generateReport ? 'generate' : 'upload', {
      label: generateReport ? t(MODALS_UPLOAD_AND_GENERATE) : t(MODALS_UPLOAD_FILE),
      sublabel: `${file.name} · ${eventName}`,
      progress: 5,
    });

    axios
      .post<UploadStartResponse>(url, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(async (response) => {
        setIsUploading(false);
        const jobId = response.data?.jobId;
        if (!jobId) {
          throw new Error('The server did not return an upload job id');
        }
        const completedJob = await pollUploadJob({
          baseUrl: API_BASE_URL,
          token,
          jobId,
          onUpdate: (job) => {
            updateProcess(processId, {
              label: job.message || (generateReport ? t(MODALS_UPLOAD_AND_GENERATE) : t(MODALS_UPLOAD_FILE)),
              sublabel: `${file.name} · ${eventName}`,
              progress: job.progress,
            });
          },
        });
        if (generateReport) {
          if (window.location.href.includes('/reports')) {
            window.dispatchEvent(new CustomEvent('reports:refresh'));
          }
          showMessage('success', t('notifications.success'), t('modals.uploadAndGenerateSuccess'));
        } else {
          showMessage('success', t('notifications.success'), t('modals.uploadSuccess'));
        }
        if (completedJob.warnings?.length) {
          showMessage('info', t('notifications.info'), completedJob.warnings.join(' '));
        }
        endProcess(processId);
        return null;
      })
      .catch((error) => {
        setIsUploading(false);
        console.error('Error uploading the file', error);
        const status = error?.response?.status;
        const fallbackMessage =
          status === 401
            ? t('auth.sessionExpired', 'Your session has expired. Please log in again.')
            : getApiErrorMessage(error, t('modals.unexpectedError'));
        showMessage('error', t('notifications.error'), fallbackMessage);
        endProcess(processId);
        return null;
      });
  };

  const handleEventSelection = (eventId: number) => {
    if (eventId === 0) {
      handleCreateNewEvent();
    } else {
      setSelectedEventId(eventId);
    }
  };

  const handleEventMouseEnter = (eventId: number) => {
    setHoveredEventId(eventId);
  };

  const handleEventMouseLeave = () => {
    setHoveredEventId(null);
  };

  const handleChangeBoat = () => {
    setSelectedBoatId(null);
    setUserEvents([]);
    setSelectedEventId(null);
  };

  const modalContainerReference = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isVisible) {
      return;
    }
    document.body.classList.add('sailtrack-modal-open');
    const handleMouseDown = (event: MouseEvent) => {
      const container = modalContainerReference.current;
      if (container && !container.contains(event.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.classList.remove('sailtrack-modal-open');
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible, onClose]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className="modal-overlay">
      <div
        className={`modal-content-reports ${showCreateEventForm ? 'transparent' : ''}`}
        role="dialog"
        aria-modal="true"
        ref={modalContainerReference}
      >
        {showCreateEventForm ? (
          <CreateEventForm
            onClose={() => setShowCreateEventForm(false)}
            onEventCreated={handleEventCreated}
          />
        ) : selectedBoatId === null ? (
          <div className="boat-selection" data-label={t('common.labels.boat').toUpperCase()}>
            <h2>{t('modals.selectBoat')}</h2>
            {isLoading ? (
              <p>{t('modals.loadingBoats')}</p>
            ) : error ? (
                      <p>{t(NOTIF_ERROR)}: {error}</p>
            ) : boats.length === 0 ? (
              <p>{t('modals.noBoats')}</p>
            ) : (
              <div className="boats-list">
                {boats.map((boat) => {
                  const isBoatSelected = selectedBoatId === boat.id;
                  return (
                    <button
                      key={boat.id}
                      type="button"
                      onClick={() => setSelectedBoatId(boat.id)}
                      className={`boat-option${isBoatSelected ? ' selected' : ''}`}
                      aria-pressed={isBoatSelected}
                    >
                      {boat.boat_name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="change-boat-container">
              <button
                type="button"
                onClick={handleChangeBoat}
                className="change-boat-button"
              >
                <ChangeBoatIcon />
                {t('modals.changeBoat')}
              </button>
            </div>
            <div className="flex-container">
              <div className="left-container">
                <div className="event-area" data-label={t('common.labels.event').toUpperCase()}>
                  <div className="create-event-container" />
                  <div className="events-list">
                    <button
                      type="button"
                      className={`create-new-event ${0 === selectedEventId ? 'selected' : ''} ${
                        0 === hoveredEventId ? 'hovered' : ''
                      }`}
                      onClick={() => handleEventSelection(0)}
                      onMouseEnter={() => handleEventMouseEnter(0)}
                      onMouseLeave={handleEventMouseLeave}
                      aria-pressed={0 === selectedEventId}
                    >
                      <CrossIcon className="plus-icon" />
                      {t('modals.createNewEvent')}
                    </button>
                    {isLoading ? (
                      <p>{t('modals.loadingEvents')}</p>
                    ) : error ? (
                      <p>{t(NOTIF_ERROR)}: {error}</p>
                    ) : userEvents.length === 0 ? null : (
                      <>
                        <div className="white-bar" />
                        {userEvents.map((item) => (
                          <button
                            type="button"
                            key={item.id}
                            className={`${item.id === selectedEventId ? 'selected' : ''} ${
                              item.id === hoveredEventId ? 'hovered' : ''
                            }`}
                            onClick={() => handleEventSelection(item.id)}
                            onMouseEnter={() => handleEventMouseEnter(item.id)}
                            onMouseLeave={handleEventMouseLeave}
                            aria-pressed={item.id === selectedEventId}
                          >
                            {item.event_name}
                            {item.event_date &&
                              ` - ${new Date(item.event_date).toLocaleDateString()}`}
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                </div>
                <div className="button-group">
                  <button
                    className={`green-button ${file ? '' : 'disabled'}`}
                    onClick={() => uploadFile(true)}
                    disabled={!file}
                  >
                    {t('modals.uploadAndGenerate')}
                  </button>
                  <button
                    className={`button-upload ${file ? '' : 'disabled'}`}
                    onClick={() => uploadFile(false)}
                    disabled={!file}
                  >
                    {t('modals.uploadFile')}
                  </button>
                </div>
              </div>
              <div className="upload-container">
                <div
                  className={`upload-area ${isDragOver ? 'drag-over' : ''} ${
                    file ? 'uploaded' : ''
                  }`}
                  onClick={handleUploadAreaClick}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      handleUploadAreaClick();
                    }
                  }}
                >
                  <UploadIcon className="upload-icon" />
                  {file ? <p>{fileName}</p> : <p>{t('modals.selectFilesHint')}</p>}
                  <input
                    type="file"
                    accept={ACCEPTED_FILE_TYPES}
                    ref={fileInputReference}
                    style={{ display: 'none' }}
                    onChange={handleFileUploadChange}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export { ModalReports };
